import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Site } from '@/types';

interface SiteContextType {
  sites: Site[];
  currentSite: Site | null;
  allSitesSelected: boolean;
  loading: boolean;
  setCurrentSite: (site: Site | null) => void;
  selectAllSites: () => void;
  refreshSites: () => Promise<void>;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export function SiteProvider({ children }: { children: ReactNode }) {
  const { user, userRole } = useAuth();
  const [sites, setSites] = useState<Site[]>([]);
  const [currentSite, setCurrentSiteState] = useState<Site | null>(null);
  const [allSitesSelected, setAllSitesSelected] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchSites = async () => {
    if (!user) {
      setSites([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      if (userRole === 'manager') {
        // Manager: fetch all accessible sites
        const { data, error } = await supabase
          .from('sites')
          .select('*')
          .order('site_name');

        // Handle case where manager has no sites yet (RLS returns empty or permission error)
        if (error) {
          // Permission denied often means no sites match the RLS policy - treat as empty
          if (error.code === '42501') {
            console.log('Manager has no accessible sites yet');
            setSites([]);
            setAllSitesSelected(true);
            setCurrentSiteState(null);
            return;
          }
          throw error;
        }
        
        const typedSites: Site[] = (data || []).map(s => ({
          id: s.id,
          manager_id: s.manager_id,
          site_name: s.site_name,
          site_address: s.site_address,
          site_manager_name: s.site_manager_name,
          site_manager_phone: s.site_manager_phone,
          site_manager_email: s.site_manager_email,
          partner_id: s.partner_id,
          created_at: s.created_at,
          updated_at: s.updated_at,
        }));
        
        setSites(typedSites);

        // Restore selected site from localStorage or select first/all
        const savedSiteId = localStorage.getItem(`selected_site_${user.id}`);
        const savedAllSites = localStorage.getItem(`all_sites_${user.id}`) === 'true';

        if (savedAllSites) {
          setAllSitesSelected(true);
          setCurrentSiteState(null);
        } else if (savedSiteId && typedSites.length > 0) {
          const savedSite = typedSites.find(s => s.id === savedSiteId);
          if (savedSite) {
            setCurrentSiteState(savedSite);
          } else {
            // Default to all sites for managers
            setAllSitesSelected(true);
            setCurrentSiteState(null);
          }
        } else if (typedSites.length > 0) {
          // Default to all sites for managers
          setAllSitesSelected(true);
          setCurrentSiteState(null);
        } else {
          // No sites - still default to all sites view
          setAllSitesSelected(true);
          setCurrentSiteState(null);
        }
      } else if (userRole === 'partner') {
        // Partner: fetch their assigned site
        const { data: partnerProfile, error: profileError } = await supabase
          .from('partner_profiles')
          .select('site_id')
          .eq('user_id', user.id)
          .single();

        if (profileError && profileError.code !== 'PGRST116') throw profileError;

        if (partnerProfile?.site_id) {
          const { data: siteData, error: siteError } = await supabase
            .from('sites')
            .select('*')
            .eq('id', partnerProfile.site_id)
            .single();

          if (siteError && siteError.code !== 'PGRST116') throw siteError;

          if (siteData) {
            const typedSite: Site = {
              id: siteData.id,
              manager_id: siteData.manager_id,
              site_name: siteData.site_name,
              site_address: siteData.site_address,
              site_manager_name: siteData.site_manager_name,
              site_manager_phone: siteData.site_manager_phone,
              site_manager_email: siteData.site_manager_email,
              partner_id: siteData.partner_id,
              created_at: siteData.created_at,
              updated_at: siteData.updated_at,
            };
            setSites([typedSite]);
            setCurrentSiteState(typedSite);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
  }, [user, userRole]);

  // Listen for invitation acceptance events to refresh sites for managers
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'invitation_accepted') {
        fetchSites();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user, userRole]);

  const setCurrentSite = (site: Site | null) => {
    setCurrentSiteState(site);
    setAllSitesSelected(false);
    if (user && site) {
      localStorage.setItem(`selected_site_${user.id}`, site.id);
      localStorage.removeItem(`all_sites_${user.id}`);
    }
  };

  const selectAllSites = () => {
    setAllSitesSelected(true);
    setCurrentSiteState(null);
    if (user) {
      localStorage.removeItem(`selected_site_${user.id}`);
      localStorage.setItem(`all_sites_${user.id}`, 'true');
    }
  };

  const refreshSites = async () => {
    await fetchSites();
  };

  return (
    <SiteContext.Provider
      value={{
        sites,
        currentSite,
        allSitesSelected,
        loading,
        setCurrentSite,
        selectAllSites,
        refreshSites,
      }}
    >
      {children}
    </SiteContext.Provider>
  );
}

export function useSiteContext() {
  const context = useContext(SiteContext);
  if (context === undefined) {
    throw new Error('useSiteContext must be used within a SiteProvider');
  }
  return context;
}
